import { registry, ServiceRegistry } from 'Api/registry/registry';
import CustomError from 'error/CustomError';

export class ServiceFactory {
  private static instance: ServiceFactory | null = null;
  private t: (key: string, options?: Record<string, any>) => string;
  private serviceCache: Record<string, any> = {};
  private static initializing: Promise<ServiceFactory> | null = null; // Promessa condivisa per l'inizializzazione

  private constructor(t: (key: string, options?: Record<string, any>) => string) {
    this.t = t;
    this.serviceCache = {};
  }

  private static buildMessage(message: string, code: string):
  { message: string; name: string; handled: boolean; code?: string; } { 
  
  return {
      code,
      message,
      name: 'Service Factory', 
      handled: false,
    }
  }

// Usa  per reinizializzare quando l'utente cambia la lingua
public static async reinitialize(t: (key: string, options?: Record<string, any>) => string): Promise<ServiceFactory> {
  ServiceFactory.instance = null;
  ServiceFactory.initializing = null;
  return ServiceFactory.initialize(t);
}

// Inizializza la factory e restituisce una Promise
public static initialize(t: (key: string, options?: Record<string, any>) => string): Promise<ServiceFactory> {
    // Se già inizializzata, restituisci immediatamente l'istanza
    if (ServiceFactory.instance) {
      return Promise.resolve(ServiceFactory.instance);
    }

    // Se è in corso l'inizializzazione, restituisci la stessa Promise
    if (ServiceFactory.initializing) {
      return ServiceFactory.initializing;
    }

    // Altrimenti, avvia l'inizializzazione
    ServiceFactory.initializing = new Promise<ServiceFactory>((resolve, reject) => {
      try {
        ServiceFactory.instance = new ServiceFactory(t);
        resolve(ServiceFactory.instance);
      } catch (error) {
        ServiceFactory.instance = null; // Resetta lo stato in caso di errore
        reject(error);
      } finally {
        ServiceFactory.initializing = null; // Resetta lo stato di initializing
      }
    });

    return ServiceFactory.initializing;
  }

  // Ottieni l'istanza della factory
  private static getInstance(): ServiceFactory {
    if (!ServiceFactory.instance) {
      throw new CustomError(this.buildMessage('ServiceFactory non è stata inizializzata. Chiama `initialize` prima.',"SF-001"));
    }
    return ServiceFactory.instance;
  }

  // Metodo generico per ottenere un servizio
  public static async getService(serviceName: keyof typeof registry) {
    const instance = ServiceFactory.getInstance();

    // Se il servizio è già in cache, lo restituiamo
    if (instance.serviceCache[serviceName]) {
      return instance.serviceCache[serviceName];
    }

    // Altrimenti, creiamo dinamicamente il servizio
    const service = await instance.createService(serviceName);
    instance.serviceCache[serviceName] = service; // Lo salviamo in cache
    return service;
  }

  // Crea dinamicamente un servizio usando il registry
  private async createService(serviceName: keyof typeof registry) {
    const serviceConfig: ServiceRegistry | undefined = registry[serviceName];
    if (!serviceConfig) {
        throw new CustomError(ServiceFactory.buildMessage(`Il servizio "${serviceName}" non è configurato nel registro.`,"SF-002"));
    }

    // Importa dinamicamente la classe del servizio
    try {
      const modulePath = serviceConfig.importPath + serviceName; // Costruisci il percorso del modulo
      const module = await import(`${modulePath}`);
      const ServiceClass = module.default; // Presuppone che la classe sia esportata come `default`
      return new ServiceClass(this.t); // Crea una nuova istanza passando `t`
    } catch (error) {
        if (error instanceof Error) {
            throw new CustomError(ServiceFactory.buildMessage(`Impossibile importare il servizio "${serviceName}" dal percorso "${serviceConfig.importPath}": ${error.message}`,"SF-002"));
        } else {
            throw new CustomError(ServiceFactory.buildMessage(`Impossibile importare il servizio "${serviceName}" dal percorso "${serviceConfig.importPath}": Errore sconosciuto.`,"SF-002"));
        }    
    }
  }
}